import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Steps, 
    Button, 
    Space, 
    Typography, 
    Tag, 
    Progress, 
    Tooltip,
    Modal,
    Form,
    Input,
    Rate,
    message,
    Divider,
    Row,
    Col,
    Statistic,
    Badge
} from 'antd';
import { 
    ClockCircleOutlined, 
    CheckCircleOutlined, 
    FireOutlined, 
    TruckOutlined,
    CloseCircleOutlined,
    DollarCircleOutlined,
    QuestionCircleOutlined,
    EditOutlined,
    StarOutlined,
    PhoneOutlined,
    EnvironmentOutlined
} from '@ant-design/icons';
import { useOrderTracking } from '../../contexts/OrderTrackingContext';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const OrderTrackingCard = ({ 
    orderId, 
    orderData, 
    showActions = true,
    compact = false,
    onOrderUpdate
}) => {
    const { 
        getTrackedOrder,
        getOrderDetails,
        getOrderTimeline,
        cancelOrder,
        rescheduleOrder,
        rateOrder,
        getStatusDisplayInfo,
        getEstimatedDeliveryTime,
        getDeliveryProgress,
        canCancelOrder,
        canRescheduleOrder,
        canRateOrder,
        loading
    } = useOrderTracking();

    const [order, setOrder] = useState(orderData);
    const [timeline, setTimeline] = useState([]);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showRescheduleModal, setShowRescheduleModal] = useState(false);
    const [showRatingModal, setShowRatingModal] = useState(false);
    const [cancelForm] = Form.useForm();
    const [rescheduleForm] = Form.useForm();
    const [ratingForm] = Form.useForm();

    // Load order details and timeline
    useEffect(() => {
        loadOrderData();
    }, [orderId]);

    // Listen for real-time updates
    useEffect(() => {
        const trackedOrder = getTrackedOrder(orderId);
        if (trackedOrder) {
            setOrder(trackedOrder);
            onOrderUpdate?.(trackedOrder);
        }
    }, [orderId, getTrackedOrder, onOrderUpdate]);

    const loadOrderData = async () => {
        try {
            const [details, timelineData] = await Promise.all([
                getOrderDetails(orderId),
                getOrderTimeline(orderId)
            ]);
            
            setOrder(details);
            setTimeline(timelineData);
        } catch (error) {
            console.error('Failed to load order data:', error);
        }
    };

    if (!order) {
        return (
            <Card loading={loading} className="order-tracking-card">
                <div className="text-center py-8">
                    <Text type="secondary">Loading order details...</Text>
                </div>
            </Card>
        );
    }

    const statusInfo = getStatusDisplayInfo(order.status);
    const estimatedTime = getEstimatedDeliveryTime(order);
    const progress = getDeliveryProgress(order);
    const canCancel = canCancelOrder(order);
    const canReschedule = canRescheduleOrder(order);
    const canRate = canRateOrder(order);

    // Handle cancel order
    const handleCancelOrder = async () => {
        try {
            const values = await cancelForm.validateFields();
            await cancelOrder(orderId, values.reason);
            setShowCancelModal(false);
            cancelForm.resetFields();
        } catch (error) {
            console.error('Failed to cancel order:', error);
        }
    };

    // Handle reschedule order
    const handleRescheduleOrder = async () => {
        try {
            const values = await rescheduleForm.validateFields();
            await rescheduleOrder(orderId, values.newDateTime);
            setShowRescheduleModal(false);
            rescheduleForm.resetFields();
        } catch (error) {
            console.error('Failed to reschedule order:', error);
        }
    };

    // Handle rate order
    const handleRateOrder = async () => {
        try {
            const values = await ratingForm.validateFields();
            await rateOrder(orderId, values.rating, values.review);
            setShowRatingModal(false);
            ratingForm.resetFields();
        } catch (error) {
            console.error('Failed to rate order:', error);
        }
    };

    // Render order status steps
    const renderStatusSteps = () => {
        const steps = [
            { key: 'pending', title: 'Order Placed' },
            { key: 'confirmed', title: 'Confirmed' },
            { key: 'preparing', title: 'Preparing' },
            { key: 'ready', title: 'Ready' },
            { key: 'out_for_delivery', title: 'Out for Delivery' },
            { key: 'delivered', title: 'Delivered' }
        ];

        const currentStepIndex = steps.findIndex(step => step.key === order.status);
        const currentStep = Math.max(0, currentStepIndex);

        return (
            <Steps
                current={currentStep}
                size="small"
                items={steps.map((step, index) => ({
                    title: step.title,
                    status: index <= currentStep ? 'finish' : 'wait',
                    icon: index === currentStep ? 
                        (statusInfo.icon === 'close-circle' ? <CloseCircleOutlined /> : 
                         statusInfo.icon === 'dollar-circle' ? <DollarCircleOutlined /> :
                         <CheckCircleOutlined />) : undefined
                }))}
            />
        );
    };

    // Render order actions
    const renderOrderActions = () => {
        if (!showActions) return null;

        return (
            <Space wrap>
                {canCancel && (
                    <Button 
                        danger 
                        onClick={() => setShowCancelModal(true)}
                        icon={<CloseCircleOutlined />}
                    >
                        Cancel Order
                    </Button>
                )}
                {canReschedule && (
                    <Button 
                        onClick={() => setShowRescheduleModal(true)}
                        icon={<EditOutlined />}
                    >
                        Reschedule
                    </Button>
                )}
                {canRate && (
                    <Button 
                        type="primary"
                        onClick={() => setShowRatingModal(true)}
                        icon={<StarOutlined />}
                    >
                        Rate Order
                    </Button>
                )}
            </Space>
        );
    };

    // Render order info
    const renderOrderInfo = () => {
        if (compact) {
            return (
                <div className="flex items-center justify-between">
                    <div>
                        <Text strong>Order #{order.order_number}</Text>
                        <br />
                        <Text type="secondary">{order.vendor?.business_name}</Text>
                    </div>
                    <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
                </div>
            );
        }

        return (
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <Title level={4} className="mb-1">
                            Order #{order.order_number}
                        </Title>
                        <Text type="secondary">
                            {order.vendor?.business_name}
                        </Text>
                    </div>
                    <Tag color={statusInfo.color} className="text-sm">
                        {statusInfo.label}
                    </Tag>
                </div>

                <Row gutter={16}>
                    <Col span={8}>
                        <Statistic
                            title="Total Amount"
                            value={order.total_amount}
                            prefix="₹"
                            precision={2}
                        />
                    </Col>
                    <Col span={8}>
                        <Statistic
                            title="Items"
                            value={order.items?.length || 0}
                            suffix="dishes"
                        />
                    </Col>
                    <Col span={8}>
                        <Statistic
                            title="Guests"
                            value={order.guest_count || 0}
                            suffix="people"
                        />
                    </Col>
                </Row>

                {estimatedTime && (
                    <div className="text-center">
                        <Text type="secondary">
                            Estimated delivery: <Text strong>{estimatedTime}</Text>
                        </Text>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <Card 
                className="order-tracking-card"
                title={renderOrderInfo()}
                extra={renderOrderActions()}
            >
                <div className="space-y-4">
                    {/* Status Description */}
                    <div className="text-center">
                        <Text type="secondary">{statusInfo.description}</Text>
                    </div>

                    {/* Progress Bar */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <Text strong>Progress</Text>
                            <Text type="secondary">{progress}%</Text>
                        </div>
                        <Progress 
                            percent={progress} 
                            strokeColor={statusInfo.color}
                            showInfo={false}
                        />
                    </div>

                    {/* Status Steps */}
                    {!compact && (
                        <div>
                            <Title level={5} className="mb-3">Order Status</Title>
                            {renderStatusSteps()}
                        </div>
                    )}

                    {/* Timeline */}
                    {timeline.length > 0 && !compact && (
                        <div>
                            <Title level={5} className="mb-3">Order Timeline</Title>
                            <div className="space-y-2">
                                {timeline.slice(0, 3).map((event, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full" />
                                        <div className="flex-1">
                                            <Text strong>{event.title}</Text>
                                            <br />
                                            <Text type="secondary" className="text-xs">
                                                {dayjs(event.created_at).fromNow()}
                                            </Text>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Delivery Info */}
                    {order.delivery_address && !compact && (
                        <div>
                            <Title level={5} className="mb-2">Delivery Address</Title>
                            <div className="flex items-start gap-2">
                                <EnvironmentOutlined className="text-gray-500 mt-1" />
                                <div>
                                    <Text>{order.delivery_address}</Text>
                                    {order.delivery_instructions && (
                                        <div className="mt-1">
                                            <Text type="secondary" className="text-xs">
                                                Note: {order.delivery_instructions}
                                            </Text>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </Card>

            {/* Cancel Order Modal */}
            <Modal
                title="Cancel Order"
                open={showCancelModal}
                onCancel={() => setShowCancelModal(false)}
                onOk={handleCancelOrder}
                okText="Cancel Order"
                okButtonProps={{ danger: true }}
            >
                <Form form={cancelForm} layout="vertical">
                    <Form.Item
                        name="reason"
                        label="Reason for cancellation"
                        rules={[{ required: true, message: 'Please provide a reason' }]}
                    >
                        <TextArea 
                            rows={3} 
                            placeholder="Please tell us why you're cancelling this order..."
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Reschedule Order Modal */}
            <Modal
                title="Reschedule Order"
                open={showRescheduleModal}
                onCancel={() => setShowRescheduleModal(false)}
                onOk={handleRescheduleOrder}
                okText="Reschedule"
            >
                <Form form={rescheduleForm} layout="vertical">
                    <Form.Item
                        name="newDateTime"
                        label="New Date & Time"
                        rules={[{ required: true, message: 'Please select new date and time' }]}
                    >
                        <Input 
                            type="datetime-local"
                            min={new Date().toISOString().slice(0, 16)}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Rate Order Modal */}
            <Modal
                title="Rate Your Order"
                open={showRatingModal}
                onCancel={() => setShowRatingModal(false)}
                onOk={handleRateOrder}
                okText="Submit Rating"
            >
                <Form form={ratingForm} layout="vertical">
                    <Form.Item
                        name="rating"
                        label="Rating"
                        rules={[{ required: true, message: 'Please provide a rating' }]}
                    >
                        <Rate />
                    </Form.Item>
                    <Form.Item
                        name="review"
                        label="Review (Optional)"
                    >
                        <TextArea 
                            rows={3} 
                            placeholder="Tell us about your experience..."
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default OrderTrackingCard;
